import { useEffect, useMemo } from 'react';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { expValEquals } from '@confluence/feature-experiments';

import type {
	useIsEligibleReactionsFeedbackPageReturnReactionCountByTimeQuery$data,
	useIsEligibleReactionsFeedbackPageReturnReactionCountByTimeQuery$variables,
} from './__generated__/useIsEligibleReactionsFeedbackPageReturnReactionCountByTimeQuery.graphql.ts';
import type {
	useIsEligibleReactionsFeedbackPageReturnGetUserLastViewedQuery$data,
	useIsEligibleReactionsFeedbackPageReturnGetUserLastViewedQuery$variables,
} from './__generated__/useIsEligibleReactionsFeedbackPageReturnGetUserLastViewedQuery.graphql.ts';

interface UseIsEligibleReactionsFeedbackPageReturnResponse {
	isEligible: boolean;
	loading: boolean;
	error?: Error;
}

const countReactionsByTime = graphql`
	query useIsEligibleReactionsFeedbackPageReturnReactionCountByTimeQuery(
		$pageId: [ID!]!
		$startTime: String!
	) {
		countGroupByPage(
			pageId: $pageId
			startTime: $startTime
			eventName: [reaction_created]
			limit: 1
		) {
			nodes {
				count
				page
			}
		}
	}
`;

const getUserLastViewed = graphql`
	query useIsEligibleReactionsFeedbackPageReturnGetUserLastViewedQuery(
		$contentIds: [ID!]!
		$startTime: String!
		$endTime: String
		$accountIds: [ID!]
	) {
		contentAnalyticsLastViewedAtByPage(
			contentIds: $contentIds
			startTime: $startTime
			endTime: $endTime
			accountIds: $accountIds
		) {
			nodes {
				contentId
				lastViewedAt
			}
		}
	}
`;

export const useIsEligibleReactionsFeedbackPageReturn = ({
	contentId,
	isContributor,
	skip = false,
	source,
}: {
	contentId: string;
	isContributor: boolean;
	skip?: boolean;
	source: 'viewPage' | 'livePage';
}): UseIsEligibleReactionsFeedbackPageReturnResponse => {
	const thirtyDaysAgo = useMemo(() => {
		const date = new Date();
		date.setDate(date.getDate() - 30);
		return date.toISOString();
	}, []);

	const { userId } = useSessionData();

	const shouldRunUserLastViewedQuery = isContributor && !skip && userId;

	const {
		data: userLastViewedData,
		loading: userLastViewedLoading,
		error: userLastViewedError,
	} = useQueryInterop<
		useIsEligibleReactionsFeedbackPageReturnGetUserLastViewedQuery$data,
		useIsEligibleReactionsFeedbackPageReturnGetUserLastViewedQuery$variables
	>(getUserLastViewed, {
		variables: {
			contentIds: [contentId],
			startTime: thirtyDaysAgo,
			accountIds: userId ? [userId] : [],
		},
		skip: !shouldRunUserLastViewedQuery,
	});

	const lastViewedAt = useMemo(() => {
		return userLastViewedData?.contentAnalyticsLastViewedAtByPage?.nodes?.[0]?.lastViewedAt;
	}, [userLastViewedData]);

	const isLastViewedInSameHour = useMemo(() => {
		if (!lastViewedAt) return false;

		const lastViewed = new Date(lastViewedAt);
		const now = new Date();

		return (
			lastViewed.getFullYear() === now.getFullYear() &&
			lastViewed.getMonth() === now.getMonth() &&
			lastViewed.getDate() === now.getDate() &&
			lastViewed.getHours() === now.getHours()
		);
	}, [lastViewedAt]);

	const shouldRunReactionCountQuery = Boolean(
		shouldRunUserLastViewedQuery &&
		!userLastViewedLoading &&
		!userLastViewedError &&
		!isLastViewedInSameHour,
	);

	const reactionQueryStartTime = lastViewedAt || thirtyDaysAgo;

	const {
		data: countReactionByTimeData,
		loading: countReactionByTimeLoading,
		error: countReactionByTimeError,
	} = useQueryInterop<
		useIsEligibleReactionsFeedbackPageReturnReactionCountByTimeQuery$data,
		useIsEligibleReactionsFeedbackPageReturnReactionCountByTimeQuery$variables
	>(countReactionsByTime, {
		variables: {
			pageId: [contentId],
			startTime: reactionQueryStartTime,
		},
		skip: !shouldRunReactionCountQuery,
	});

	const isLoading = countReactionByTimeLoading || userLastViewedLoading;
	const allErrors = countReactionByTimeError || userLastViewedError;

	const isEligible = useMemo(() => {
		if (skip || !isContributor) {
			return false;
		}

		if (isLoading || allErrors) {
			return false;
		}

		const reactionCount = countReactionByTimeData?.countGroupByPage?.nodes?.[0]?.count || 0;
		if (reactionCount <= 0) {
			return false;
		}

		// Check experiment cohort
		// Given this experiment is fired from both Live Page and
		// Regular Page, from two different code path, we check
		// experiment flag in eligiblity hook.
		return expValEquals('cc-engagement-reactions-feedback-page-return-exp', 'cohort', 'experiment');
	}, [countReactionByTimeData, skip, isContributor, isLoading, allErrors]);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	useEffect(() => {
		if (isEligible) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'reactionsFeedbackPageReturnExp',
					attributes: {
						source,
					},
				},
			}).fire();
		}
	}, [isEligible, createAnalyticsEvent, source]);

	return {
		isEligible,
		loading: isLoading,
		error: allErrors,
	};
};
